<template>
  <div class="col-12 nopads">
    <span v-if="showTitle" class="span-title">{{ $t('observations.order') }}</span>
    <multiselect
      v-model="selectedOrders"
      :options="orderOptions"
      :multiple="!singleMode"
      :close-on-select="singleMode"
      label="text"
      track-by="value"
      :searchable="true"
      :internal-search="true"
      :selected-label="$t('common.selected')"
      :select-label="$t('common.select')"
      :deselect-label="$t('common.remove_selection')"
      :placeholder="$t('orders.select_status')"
      :disabled="loading > 0 || !contractIds || disabled"
      :maxHeight="maxHeight"
      @select="handleInput"
      @remove="handleInput"
    >
      <template #noResult>
        <span>{{ $t('common.not_found') }}</span>
      </template>
      <template #noOptions>
        <span>{{ $t('common.no_results') }}</span>
      </template>
    </multiselect>
    <div v-if="showOpenOrPlannedOption" class="form-check mt-1">
      <input
        id="openAndInProgressCheckbox"
        v-model="filterOpenOrPlanned"
        type="checkbox"
        class="form-check-input"
      />
      <label class="form-check-label" for="openAndInProgressCheckbox" style="font-size: .9em">
        {{ $t('map_filters.open_and_in_progress') }}
      </label>
    </div>
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {orderMixin} from "../mixins/OrderMixin"

export default {
    name: 'ContractOrderMultiselect',
    mixins: [restApi, orderMixin],
    props: {
        preSelectedOrder: {
            type: Number,
            default: function () {
                return null
            }
        },
        contractIds: {
            type: Array,
            default: () => [],
        },
        openOnly: {
            type: Boolean,
            default: false
        },
        singleMode: {
            type: Boolean,
            default: false
        },
        showTitle: {
            type: Boolean,
            default: true
        },
        noOrderOption: {
            type: Boolean,
            default: false
        },
        maxHeight: {
            type: String,
            default: '30em'
        },
        openOrPlannedOnly: {
            type: Boolean,
            default: false
        },
        onlyEly: {
            type: Boolean,
            default: false
        },
        showOpenOrPlannedOption: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
    },

    data: function () {
        return {
            loading: 0,
            results: [],
            orderOptions: [],
            selectedOrders: [],
            filterOpenOrPlanned: false,
        }
    },

    watch: {
        contractIds: {
            handler(newVal, oldVal = []) {
                if (this.arraysEqual(newVal, oldVal)) return
                this.fetchOrders()
            }
        },
        filterOpenOrPlanned() {
            this.fetchOrders()
        }
    },

    mounted() {
        if (this.preSelectedOrder) {
            this.selectedOrders.push({value: this.preSelectedOrder})
        }
        this.fetchOrders()
    },

    methods: {
        arraysEqual(a = [], b = []) {
            if (a.length !== b.length) return false
            for (let i = 0; i < a.length; i++) {
                if (a[i] !== b[i]) return false
            }
            return true
        },

        handleInput: function () {
            if (this.singleMode) {
                let selectedOrder = null;
                let hasNoOrder = false;
                if (this.selectedOrders) {
                    if(this.selectedOrders.value === null) {
                        hasNoOrder = true;
                    } else {
                        this.results.forEach(order => {
                            if (order.id === this.selectedOrders.value) {
                                selectedOrder = order;
                            }
                        });
                    }
                }
                this.$emit('ordersSelected', selectedOrder);
                this.$emit('noOrderSelected', hasNoOrder);
            } else {
                let selected = [];
                let hasNoOrder = false;
                this.selectedOrders.forEach(item => {
                    if (item.value === null) {
                        hasNoOrder = true;
                    } else {
                        this.results.forEach(order => {
                            if (order.id === item.value) {
                                selected.push(order);
                            }
                        });
                    }
                });
                this.$emit('ordersSelected', selected);
                this.$emit('noOrderSelected', hasNoOrder);
            }
        },

        fetchOrders: function () {
            this.results = []
            this.orderOptions = []
            if (this.contractIds && this.contractIds.length > 0) {
                if (this.contractIds && this.contractIds.length > 0 && this.contractIds[0]) {
                    this.loading++
                    let params = {
                        contract_ids: this.contractIds
                    }
                    if (this.openOnly) {
                        params.open = 1
                    }
                    if(this.openOrPlannedOnly || this.filterOpenOrPlanned) {
                        params.open_or_planned = 1
                    }
                    this.restFetchParams(this.orderUrl, params, this.handleOrders)
                }
            }
        },

        handleOrders: function (response) {
            this.loading--
            if (response && response.data) {
                this.orderOptions = []
                this.results = response.data
                if(this.noOrderOption) {
                    this.orderOptions.push({
                        value: null,
                        text: this.$t('daily_summary.no_order'),
                    })
                }
                // Set options and restore selection if possible
                for (let i = 0; i < response.data.length; i++) {
                    const order = response.data[i]
                    if (!this.onlyEly || order.external_tasks.length > 0) {
                        this.orderOptions.push(
                            {
                                value: order.id,
                                text: order.id + ' - ' + this.getOrderStringWithInfo(order)
                            }
                        )
                    }
                }
                let tempSelected = []
                for (let i = 0; i < this.selectedOrders.length ; i++) {
                    // Search if options has selected item
                    const selected = this.selectedOrders[i]
                    const existingOption = this.orderOptions.find(item => item.value === selected.value)
                    if (existingOption) {
                        tempSelected.push(existingOption)
                    }
                }
                this.selectedOrders = tempSelected
                // Convert to orders for parent
                let result = []
                this.selectedOrders.forEach(item => {
                    const selectedOrder = this.results.find(o => o.id === item.value)
                    if (selectedOrder) {
                        result.push(selectedOrder);
                    }
                })
                this.$emit('ordersSelected', result);
            }
        },

        reset: function () {
            this.results = []
            this.orderOptions = []
            this.selectedOrders = []
        },

        getOrderInfo(order) {
            if (order.info) {
                return order.info.length > 70 ? (order.info.substr(0,65) + '...') : order.info
            }
            return ''
        },

        getOrderString(order) {
            let result = this.showExecutorText ? this.getOrderAndExecutorStr(order) : this.getOrderStr(order)
            result += (this.showInfo && order.info) ? ' - ' + this.getOrderInfo(order) : ''
            return result
        }

    }
}
</script>
<style scoped>
</style>
